import { formatMoney } from '@lib/money'
import { getShortName } from '@/utils/userNames'
import Modal from '@components/Modal'
import { GetPayUpType, GetUserType } from '@orbit/api-types'
import React from 'react'
import {
    Actions,
    Button,
    ConfirmText,
    Content,
    CrossUserWarning,
    SummaryAmount,
    SummaryArrow,
    SummaryAvatars,
    SummaryCard,
    SummaryCurrencyCode,
    SummaryName,
} from './DeletePayUpConfirmModal.styles'
import { backgroundOrange } from '@/theme/colors'
import { Avatar } from '@components/Avatar'
import { TestIds } from '@/testIds'

interface DeletePayUpConfirmModalProps {
    isOpen: boolean
    onClose: () => void
    onConfirm: () => void
    payUp: GetPayUpType | null
    members: GetUserType[]
    currentUserId: number
    showCurrencyCode: boolean
    isSubmitting?: boolean
}

const formatAmount = formatMoney

const DeletePayUpConfirmModal: React.FC<DeletePayUpConfirmModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    payUp,
    members,
    currentUserId,
    showCurrencyCode,
    isSubmitting = false,
}) => {
    if (!payUp) return null

    const fromMember = members.find(m => m.id === payUp.fromUserId)
    const toMember = members.find(m => m.id === payUp.toUserId)
    const isCurrentUserParticipant =
        payUp.fromUserId === currentUserId || payUp.toUserId === currentUserId
    const showCrossUserWarning = !isCurrentUserParticipant

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Delete payup?"
            size="small"
            backgroundColor={backgroundOrange}
            closeOnBackdropClick={false}
        >
            <Content>
                <ConfirmText>
                    This will permanently delete the following payup. This action cannot be undone.
                </ConfirmText>

                <SummaryCard>
                    <SummaryAvatars>
                        <Avatar size="lg" user={fromMember} />
                        <SummaryName>{getShortName(fromMember)}</SummaryName>
                        <SummaryArrow>→</SummaryArrow>
                        <Avatar size="lg" user={toMember} />
                        <SummaryName>{getShortName(toMember)}</SummaryName>
                    </SummaryAvatars>
                    <SummaryAmount>
                        {formatAmount(payUp.amount, payUp.currencyCode)}
                        {showCurrencyCode && (
                            <SummaryCurrencyCode>
                                {payUp.currencyCode.toUpperCase()}
                            </SummaryCurrencyCode>
                        )}
                    </SummaryAmount>
                </SummaryCard>

                {showCrossUserWarning && (
                    <CrossUserWarning>
                        This payup is between <strong>{getShortName(fromMember)}</strong> and{' '}
                        <strong>{getShortName(toMember)}</strong> — deleting will affect their balances.
                    </CrossUserWarning>
                )}

                <Actions>
                    <Button type="button" variant="secondary" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        variant="danger"
                        onClick={onConfirm}
                        disabled={isSubmitting}
                        data-testid={TestIds.common.popupConfirm}
                    >
                        {isSubmitting ? 'Deleting...' : 'Delete payup'}
                    </Button>
                </Actions>
            </Content>
        </Modal>
    )
}

export default DeletePayUpConfirmModal
